// Drawdown-playbook state derivation. Pure functions only — file IO lives in
// log.ts, orchestration in app/api/playbook/route.ts.
//
// Rung ladder (Steve redline 2026-05-15, see feedback_benchmark_relative_not_a_trigger.md):
// trailing the index is NOT a trigger — Triumph charges fees + holds cash, so
// benchmark drift in bull tape is structural, not a signal. The rungs key off
// drawdown from peak (capital impairment) and avg composite (quality erosion).
// Book − SPY YTD is informational only.

import { compositeScore, prodQuality } from '../composite-score';

export type RungId = 'armed' | 'watch' | 'reduce' | 'halt';

export interface RungSpec {
  id: RungId;
  label: string;
  ddPctThreshold: number | null;     // ddFromPeakPct strictly less than → fires (negative, e.g. -10)
  avgCompositeThreshold: number | null;
  actions: string[];
}

export const RUNGS: RungSpec[] = [
  {
    id: 'armed',
    label: 'ARMED',
    ddPctThreshold: null,
    avgCompositeThreshold: null,
    actions: ['no action', 'review weekly'],
  },
  {
    id: 'watch',
    label: 'WATCH',
    ddPctThreshold: -10,
    avgCompositeThreshold: 2.0,
    actions: ['trim weakest Q name', 'hedge most concentrated position'],
  },
  {
    id: 'reduce',
    label: 'REDUCE',
    ddPctThreshold: -15,
    avgCompositeThreshold: 1.5,
    actions: ['collar top 3 positions', 'raise cash to 15%'],
  },
  {
    id: 'halt',
    label: 'HALT',
    ddPctThreshold: -20,
    avgCompositeThreshold: 1.0,
    actions: ['exit Q<0 names', 'halt new entries', 'send investor note'],
  },
];

const RUNG_ORDER: RungId[] = ['armed', 'watch', 'reduce', 'halt'];

export interface PlaybookInputs {
  bookYtdFrac: number | null;          // 0.067 means +6.7%
  spyYtdFrac: number | null;
  ddFromPeakFrac: number | null;       // -0.034 means -3.4%
  weights: Record<string, number>;     // symbol → weight (0..1, includes cash if you want, excluded if you don't)
  cashWeight: number;
  compositeBySymbol: Record<string, number>;
}

export interface PlaybookCurrent {
  asOf: string;
  state: RungId;
  bookMinusSpyYtdPp: number | null;    // (bookYtd - spyYtd) × 100, in percentage points
  ddFromPeakPct: number | null;        // ddFromPeakFrac × 100, negative number
  avgComposite: number | null;
  triggers: Array<{ rung: RungId; reason: 'dd' | 'composite' }>;
}

export function deriveRung(ddPct: number | null, avgComposite: number | null): { state: RungId; triggers: Array<{ rung: RungId; reason: 'dd' | 'composite' }> } {
  const triggers: Array<{ rung: RungId; reason: 'dd' | 'composite' }> = [];
  let highest: RungId = 'armed';
  for (const r of RUNGS) {
    if (r.id === 'armed') continue;
    let fired = false;
    if (r.ddPctThreshold != null && ddPct != null && ddPct < r.ddPctThreshold) {
      triggers.push({ rung: r.id, reason: 'dd' });
      fired = true;
    }
    if (r.avgCompositeThreshold != null && avgComposite != null && avgComposite < r.avgCompositeThreshold) {
      triggers.push({ rung: r.id, reason: 'composite' });
      fired = true;
    }
    if (fired && RUNG_ORDER.indexOf(r.id) > RUNG_ORDER.indexOf(highest)) {
      highest = r.id;
    }
  }
  return { state: highest, triggers };
}

/** Weighted avg composite over equity holdings (cash excluded, weights renormalised). */
export function avgCompositeFromHoldings(
  weights: Record<string, number>,
  cashWeight: number,
  compositeBySymbol: Record<string, number>,
): number | null {
  const equityWeight = Math.max(0, 1 - (cashWeight || 0));
  if (equityWeight <= 0) return null;
  let weightSum = 0;
  let compositeSum = 0;
  for (const [sym, w] of Object.entries(weights)) {
    if (!Number.isFinite(w) || w <= 0) continue;
    const c = compositeBySymbol[sym];
    if (!Number.isFinite(c)) continue;
    weightSum += w;
    compositeSum += w * c;
  }
  if (weightSum <= 0) return null;
  return compositeSum / weightSum;
}

/** Wraps compositeScore so caller can pass raw scores.json stock rows. */
export function compositeFor(stock: { qualityScore?: number | null; qualityScoreBlended?: number | null; valuationScore?: number | null }): number | null {
  // Q axis = production blended (qualityScoreBlended ?? qualityScore).
  return compositeScore(prodQuality(stock), stock.valuationScore ?? null);
}

export function computeCurrent(input: PlaybookInputs, asOf: string): PlaybookCurrent {
  const bookMinusSpyYtdPp = input.bookYtdFrac != null && input.spyYtdFrac != null
    ? (input.bookYtdFrac - input.spyYtdFrac) * 100
    : null;
  const ddFromPeakPct = input.ddFromPeakFrac != null ? input.ddFromPeakFrac * 100 : null;
  const avgComposite = avgCompositeFromHoldings(input.weights, input.cashWeight, input.compositeBySymbol);
  const { state, triggers } = deriveRung(ddFromPeakPct, avgComposite);
  return { asOf, state, bookMinusSpyYtdPp, ddFromPeakPct, avgComposite, triggers };
}
